import React, { useRef } from 'react';
import { Dispatch } from 'redux';
import { Popper } from 'react-popper';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { RootAction, State } from 'store/state';
import { setPrivateBulletinSuccesfullyPromotedPopover, PromotePrivateBulletinViewSource } from 'store/state/app/actions';
import { createNextAvailablePromotionDateSelector, currentPromotionPlanIntervalHoursSelector, isPrivateBulletinSuccesfullyPromotedPopoverSelector, isPromotePrivateBulletinMutationLoadingSelector, makeIsBulletinBasicPromotionSelector } from 'store/state/app/private-user-monetization/selectors';
import { BulletinPoiType, DealType } from 'utils/entities';
import { ManualPromoteBulletinButton, ManualPromoteBulletinButtonProps } from './ManualPromoteBulletinButton';
import { Autohide } from '../Autohide';
import { SuccessPopoverMessage } from './PopoverMessage';
import { SuccessMessage } from './Message';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';
import { usePromote } from '../usePromote';

type SmartManualPromoteBulletinButtonProps = Omit<ManualPromoteBulletinButtonProps, 'onClick'> & {
  bulletinId: string;
  bulletinType: BulletinPoiType;
  dealType: DealType;
  viewSource: PromotePrivateBulletinViewSource;
  isPromoteBulletinLoading: boolean;
  successPopoverState: {
    isOpen: boolean;
    isPaidAndRunning?: boolean;
  };
  onCloseSuccessPopover: () => void;
  isFreePlan?: boolean;
  hoursInterval?: number;
};

const SmartManualPromoteBulletinButton: React.FC<SmartManualPromoteBulletinButtonProps> = props => {
  const {
    promotedUntil,
    mode,
    size,
    className,
    successPopoverState,
    onCloseSuccessPopover,
    bulletinId,
    bulletinType,
    viewSource,
    dealType,
    isPromoteBulletinLoading,
    fullWidth,
    isFreePlan,
    hoursInterval,
  } = props;
  const { t } = useLocale();
  const buttonRef = useRef(null);
  const { route } = useRoute();
  const handlePromoteClick = usePromote(bulletinId, bulletinType, dealType, viewSource);

  return route.name === Route.ManageBulletins && isFreePlan ? (
    <>
      <ManualPromoteBulletinButton
        ref={buttonRef}
        className={className}
        mode={mode}
        size={size}
        promotedUntil={promotedUntil}
        onClick={handlePromoteClick}
        disabled={isPromoteBulletinLoading}
        fullWidth={fullWidth}
      />
      <Autohide
        show={successPopoverState.isOpen}
        seconds={10}
        onClose={onCloseSuccessPopover}
      >
        <SuccessMessage bulletinId={bulletinId} onClose={onCloseSuccessPopover} />
      </Autohide>
    </>
  ) : (
    <>
      <ManualPromoteBulletinButton
        ref={buttonRef}
        className={className}
        mode={mode}
        size={size}
        withTimer
        promotedUntil={promotedUntil}
        onClick={handlePromoteClick}
        disabled={isPromoteBulletinLoading}
        fullWidth={fullWidth}
      />
      <Popper
        referenceElement={buttonRef.current}
        placement="top"
      >
        {({ ref, style }) => (
          <Autohide
            show={successPopoverState.isOpen}
            seconds={5}
            onClose={onCloseSuccessPopover}
          >
            <SuccessPopoverMessage
              ref={ref}
              style={{ ...style, top: -4 }}
              isPaidAndRunning={successPopoverState.isPaidAndRunning}
              onClose={onCloseSuccessPopover}
              message={successPopoverState.isPaidAndRunning ? t('privateBulletinPromotion.successfullyPromotedPaidAndRunning', { hours: hoursInterval }) : t('privateBulletinPromotion.successfullyPromoted')}
            />
          </Autohide>
        )}
      </Popper>
    </>
  );
};

const mapStateToProps = (state: State, ownProps: { bulletinId: string }) => ({
  promotedUntil: createNextAvailablePromotionDateSelector(ownProps.bulletinId)(state),
  successPopoverState: isPrivateBulletinSuccesfullyPromotedPopoverSelector(state),
  isPromoteBulletinLoading: isPromotePrivateBulletinMutationLoadingSelector(state),
  isFreePlan: makeIsBulletinBasicPromotionSelector(ownProps.bulletinId)(state),
  hoursInterval: currentPromotionPlanIntervalHoursSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onCloseSuccessPopover: () => dispatch(setPrivateBulletinSuccesfullyPromotedPopover({ isOpen: false })),
});

export default connect(mapStateToProps, mapDispatchToProps)(SmartManualPromoteBulletinButton);
